import { Type } from 'class-transformer';
import { plainToInstance } from 'class-transformer';

import { EAccountUpdateType } from '../../account';
import { ApiProperty } from '../../codegen';
import { jsonParseSafe } from '../../helpers';
import {
  IsBoolean,
  IsDate,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  Min,
  ValidateNested,
  validateSync,
} from '../../validators';

import {
  EDevicePoolMemberOverride,
  EDevicePoolSourceKind,
  ERestrictionEventType,
  EUserBanSource,
} from './security.enums';

export class CreateUserBanDto {
  @ApiProperty()
  @IsBoolean({ groups: ['error'] })
  isPermanent: boolean;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  banUntil?: Date;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  reason?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsEnum(EUserBanSource, { groups: ['error'] })
  source?: EUserBanSource;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsBoolean({ groups: ['error'] })
  applyLinkedKycAutoBlock?: boolean;
}

export class LiftUserBanDto {
  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  reason?: string;
}

export class UserBanStatusDto {
  @ApiProperty()
  @IsBoolean({ groups: ['error'] })
  isActive: boolean;

  @ApiProperty()
  @IsBoolean({ groups: ['error'] })
  isPermanent: boolean;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  banUntil?: Date | null;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  reason?: string | null;
}

export class BlockedAccountCookieDto extends UserBanStatusDto {}

export class UserBanListItemDto extends UserBanStatusDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  userId: number;

  @ApiProperty()
  @IsEnum(EUserBanSource, { groups: ['error'] })
  source: EUserBanSource;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  actorLabel?: string | null;

  @ApiProperty()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  activatedAt: Date;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  liftedAt?: Date | null;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  linkedBanOriginUserId?: number | null;
}

export class SuspiciousAccountListItemDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  userId: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  suspiciousScoreMilli: number;

  @ApiProperty()
  @IsBoolean({ groups: ['error'] })
  isSuspicious: boolean;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  linkedBannedUsersCount: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  linkedIpsCount: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  latestMatchedAt?: Date | null;
}

export class UserLinkEvidenceDto {
  @ApiProperty()
  @IsString({ groups: ['error'] })
  kind: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  summary: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  bannedUserId?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  normalizedIp?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  weightMilli?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  matchedAt?: Date;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  sourceBanActivatedAt?: Date;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  oauthProvider?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  relatedUserCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  pairUserId?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  linkStrengthMilli?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  sharedIpCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  sharedUaOnIpCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  concurrentWindowsCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  subnetKey?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  subnetFamily?: 'ipv4_24' | 'ipv6_64';

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  ipMetaAsn?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  ipMetaCountry?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsBoolean({ groups: ['error'] })
  ipMetaIsVpn?: boolean;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsBoolean({ groups: ['error'] })
  ipMetaIsHosting?: boolean;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  tlsFingerprintHash?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  tlsFingerprintMatchCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsString({ groups: ['error'] })
  deviceIdHash?: string;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  deviceMatchCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  collusionSignalCount?: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  devicePoolId?: number;
}

export class UserPairLinkListItemDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  userIdLow: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  userIdHigh: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  linkStrengthMilli: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  sharedIpCount: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  sharedUaOnIpCount: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  concurrentWindowsCount: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  lastMatchedAt?: Date | null;
}

export class DevicePoolMemberDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  userId: number;

  @ApiProperty()
  @IsEnum(EDevicePoolSourceKind, { groups: ['error'] })
  joinedVia: EDevicePoolSourceKind;

  @ApiProperty()
  @IsEnum(EDevicePoolMemberOverride, { groups: ['error'] })
  override: EDevicePoolMemberOverride;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  linkStrengthMilli: number;

  @ApiProperty()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  joinedAt: Date;
}

export class DevicePoolListItemDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  poolId: number;

  @ApiProperty()
  @IsEnum(EDevicePoolSourceKind, { groups: ['error'] })
  sourceKind: EDevicePoolSourceKind;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  rootUserId: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  memberCount: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(0)
  linkStrengthMilli: number;

  @ApiProperty({ required: false })
  @IsOptional()
  @Type(() => Date)
  @IsDate({ groups: ['error'] })
  lastRecomputedAt?: Date | null;
}

export class DevicePoolDetailsDto extends DevicePoolListItemDto {
  @ApiProperty()
  @Type(() => DevicePoolMemberDto)
  @ValidateNested({ each: true, groups: ['error'] })
  members: DevicePoolMemberDto[];
}

export class MergeDevicePoolsDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  primaryPoolId: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  @Min(1)
  secondaryPoolId: number;
}

export class AccountRestrictionSseDto {
  @ApiProperty()
  @IsEnum(EAccountUpdateType, { groups: ['error'] })
  updateType: EAccountUpdateType.restriction;

  @ApiProperty()
  @IsEnum(ERestrictionEventType, { groups: ['error'] })
  type: ERestrictionEventType;

  @ApiProperty()
  @Type(() => UserBanStatusDto)
  @ValidateNested({ groups: ['error'] })
  banStatus: UserBanStatusDto;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseDtoValue<T>(value: unknown, Dto: new () => T): T | undefined {
  if (!isRecord(value)) {
    return;
  }

  const dto = plainToInstance(Dto, value);
  const validationErrors = validateSync(dto as object, {
    whitelist: true,
  });

  if (validationErrors.length > 0) {
    return;
  }

  return dto;
}

export function parseBlockedAccountPayload(
  value: unknown,
): BlockedAccountCookieDto | undefined {
  const candidate =
    isRecord(value) && 'banStatus' in value ? value.banStatus : value;
  const dto = parseDtoValue(candidate, BlockedAccountCookieDto);

  if (!dto?.isActive) {
    return;
  }

  return dto;
}

export function parseBlockedAccountCookieValue(
  value: string | false | undefined,
): BlockedAccountCookieDto | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    return;
  }

  const decodedValue = decodeURIComponent(value);
  return parseBlockedAccountPayload(jsonParseSafe<unknown>(decodedValue));
}

export function serializeBlockedAccountCookieValue(
  banStatus: BlockedAccountCookieDto,
): string {
  return encodeURIComponent(JSON.stringify(banStatus));
}

export function parseAccountRestrictionPayload(
  value: unknown,
): AccountRestrictionSseDto | undefined {
  const dto = parseDtoValue(value, AccountRestrictionSseDto);

  if (dto?.updateType !== EAccountUpdateType.restriction) {
    return;
  }

  const banStatus = parseBlockedAccountPayload(dto.banStatus);
  if (!banStatus) {
    return;
  }

  dto.banStatus = banStatus;
  return dto;
}
